import { motion } from 'framer-motion';
import { Compass, Gauge, KeyRound, User } from 'lucide-react';
import { StatusBadge } from '@/components/common/status-badge';
import { VehicleThumb } from '@/components/vehicles/vehicle-thumb';
import { refDoc, refId } from '@/lib/api/refs';
import { fullName, percent, speed } from '@/lib/format';
import { useUserDirectory } from '@/lib/queries/admin';
import { useVehicleDirectory } from '@/lib/queries/vehicles';
import { contactSummary, type LiveVehicle } from '@/lib/vehicle-status';
import type { UserRef } from '@/types/api';

/**
 * Compact card shown while the cursor rests on a marker. Deliberately lighter
 * than the bottom panel — enough to identify the vehicle and its driver without
 * having to click.
 */
export function VehicleHoverCard({ entry }: { entry: LiveVehicle }) {
  const { data: vehicles } = useVehicleDirectory();
  const { data: users } = useUserDirectory();

  // The live projection carries no driver, so resolve through the full record.
  const full = vehicles?.byId.get(entry.vehicle.id);
  const driverRef = full?.assignedDriverId ?? entry.vehicle.assignedDriverId;
  const driverId = refId(driverRef);
  const driver =
    refDoc<UserRef>(driverRef) ?? (driverId ? users?.byId.get(driverId) : undefined);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 2, scale: 0.98 }}
      transition={{ duration: 0.14, ease: [0.2, 0.8, 0.2, 1] }}
      className="w-[248px] origin-bottom rounded-xl bg-popover p-3 shadow-float"
    >
      <div className="flex items-start gap-2.5">
        <VehicleThumb
          make={entry.vehicle.make}
          model={entry.vehicle.model}
          status={entry.status}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold tracking-tight">
            {entry.vehicle.make} {entry.vehicle.model}
          </p>
          <p className="tnum truncate text-[11px] text-muted-foreground">
            ID : {entry.vehicle.plateNumber}
          </p>
        </div>
        <StatusBadge status={entry.status} />
      </div>

      <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
        <Row icon={Gauge} label="Speed" value={speed(entry.speedKph)} />
        <Row icon={Compass} label="Heading" value={`${Math.round(entry.headingDeg)}°`} />
        <Row icon={KeyRound} label="Ignition" value={entry.ignition ? 'On' : 'Off'} />
        <Row
          icon={Gauge}
          label="Battery"
          value={percent(entry.vehicle.lastKnown?.batteryPercent)}
        />
        <div className="col-span-2">
          <Row icon={User} label="Driver" value={driver ? fullName(driver) : 'Unassigned'} />
        </div>
      </dl>

      <p className="tnum mt-2.5 border-t pt-2 text-[10.5px] text-muted-foreground">
        {entry.vehicle.group ? `${entry.vehicle.group} · ` : ''}
        {contactSummary(entry)}
      </p>
    </motion.div>
  );
}

function Row({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Gauge;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="label-micro flex items-center gap-1 text-[10px]">
        <Icon className="size-3" />
        {label}
      </p>
      <p className="tnum truncate text-[12px] font-semibold">{value}</p>
    </div>
  );
}
